<script setup lang="ts">
import {onBeforeUnmount, ref } from 'vue';
import { useRafFn } from '@vueuse/core';
import type { MapApi } from './MapView.vue';

const props = defineProps<{
  mapApi: MapApi,
}>();

const rotationDirection = ref<number>();
const zoomDirection = ref<number>();

const rotateStep = Math.PI / 36;
const rotateSpeed = Math.PI / 1800;

const zoomStep = 0.2;
const zoomSpeed = 0.002;

const holdStartDelayMs = 140;
const tapEasingDurationMs = 110;

let holdStartTimeoutId: ReturnType<typeof setTimeout> | undefined;
let pressedDirection: number | undefined;

let zoomHoldStartTimeoutId: ReturnType<typeof setTimeout> | undefined;
let pressedZoomDirection: number | undefined;

const maxFrameDeltaMs = 50; // ~3 frames worth; tune as needed

useRafFn(({ delta }) => {
  const clampedDelta = Math.min(delta, maxFrameDeltaMs);

  if (rotationDirection.value !== undefined) {
    props.mapApi.rotateBy(
      rotationDirection.value * rotateSpeed * clampedDelta
    );
  }

  if (zoomDirection.value !== undefined) {
    props.mapApi.zoomBy(
      zoomDirection.value * zoomSpeed * clampedDelta
    );
  }
});

function onPointerDownCapture(event: PointerEvent) {
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
}

function onRotateButtonDown(direction: number) {
  pressedDirection = direction;
  rotationDirection.value = undefined;

  if (holdStartTimeoutId) {
    clearTimeout(holdStartTimeoutId);
  }

  holdStartTimeoutId = setTimeout(() => {
    rotationDirection.value = direction;
    holdStartTimeoutId = undefined;
  }, holdStartDelayMs);
}

function onRotateButtonUp() {
  if (holdStartTimeoutId) {
    clearTimeout(holdStartTimeoutId);
    holdStartTimeoutId = undefined;

    if (pressedDirection !== undefined) {
      props.mapApi.rotateByAnimated(
        pressedDirection * rotateStep,
        tapEasingDurationMs
      );
    }
  }

  rotationDirection.value = undefined;
  pressedDirection = undefined;
}

function onRotateButtonCancel() {
  if (holdStartTimeoutId) {
    clearTimeout(holdStartTimeoutId);
    holdStartTimeoutId = undefined;
  }

  rotationDirection.value = undefined;
  pressedDirection = undefined;
}

function onZoomButtonDown(direction: number) {
  pressedZoomDirection = direction;
  zoomDirection.value = undefined;

  if (zoomHoldStartTimeoutId) {
    clearTimeout(zoomHoldStartTimeoutId);
  }

  zoomHoldStartTimeoutId = setTimeout(() => {
    zoomDirection.value = direction;
    zoomHoldStartTimeoutId = undefined;
  }, holdStartDelayMs);
}

function onZoomButtonUp() {
  if (zoomHoldStartTimeoutId) {
    clearTimeout(zoomHoldStartTimeoutId);
    zoomHoldStartTimeoutId = undefined;

    if (pressedZoomDirection !== undefined) {
      props.mapApi.zoomByAnimated(
        pressedZoomDirection * zoomStep,
        tapEasingDurationMs
      );
    }
  }

  zoomDirection.value = undefined;
  pressedZoomDirection = undefined;
}

function onZoomButtonCancel() {
  if (zoomHoldStartTimeoutId) {
    clearTimeout(zoomHoldStartTimeoutId);
    zoomHoldStartTimeoutId = undefined;
  }

  zoomDirection.value = undefined;
  pressedZoomDirection = undefined;
}

function resetNorth() {
  props.mapApi.setBearingAnimated(0, tapEasingDurationMs);
}

onBeforeUnmount(() => {
  if (holdStartTimeoutId) {
    clearTimeout(holdStartTimeoutId);
  }

  if (zoomHoldStartTimeoutId) {
    clearTimeout(zoomHoldStartTimeoutId);
  }
});
</script>

<template>
  <div class="map-rotation-controls">
    <button type="button" class="rotation-button" aria-label="Rotate clockwise"
      @pointerdown="(e) => { onPointerDownCapture(e); onRotateButtonDown(1) }" @pointerup="onRotateButtonUp"
      @pointerleave="onRotateButtonCancel" @pointercancel="onRotateButtonCancel">
      <i class="pi pi-undo"></i>
    </button>

    <button type="button" class="rotation-button" aria-label="Reset north" @click="resetNorth">
      <i class="pi pi-compass"></i>
    </button>

        <button type="button" class="rotation-button" aria-label="Rotate counterclockwise"
      @pointerdown="(e) => { onPointerDownCapture(e); onRotateButtonDown(-1) }" @pointerup="onRotateButtonUp"
      @pointerleave="onRotateButtonCancel" @pointercancel="onRotateButtonCancel">
      <i class="pi pi-refresh"></i>
    </button>

    <button type="button" class="rotation-button" aria-label="Zoom out"
      @pointerdown="(e) => { onPointerDownCapture(e); onZoomButtonDown(-1) }" @pointerup="onZoomButtonUp"
      @pointerleave="onZoomButtonCancel" @pointercancel="onZoomButtonCancel">
      <i class="pi pi-minus"></i>
    </button>

    <button type="button" class="rotation-button" aria-label="Zoom in"
      @pointerdown="(e) => { onPointerDownCapture(e); onZoomButtonDown(1) }" @pointerup="onZoomButtonUp"
      @pointerleave="onZoomButtonCancel" @pointercancel="onZoomButtonCancel">
      <i class="pi pi-plus"></i>
    </button>
  </div>
</template>

<style scoped>
.map-rotation-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rotation-button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.0rem;
  min-width: 2.0rem;
  box-sizing: border-box;
  padding: 0.2rem;
  border: 1px solid var(--p-primary-color);
  border-radius: 6px;
  background: var(--p-content-background, #fff);
  color: var(--p-primary-color);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  font: inherit;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.rotation-button:hover {
  border-color: var(--p-primary-color);
}
</style>
